import { useState, useEffect, useCallback } from 'react';
import { residentApi } from '../../../services/residentApi';
import { Category, CategoryWithChildren } from '../../../../crm/components/category/types';

/**
 * Hook to fetch categories for resident app
 * Converts flat categories to CategoryWithChildren format for compatibility with formatCategoryName
 */
export function useResidentCategories() {
  const [categories, setCategories] = useState<CategoryWithChildren[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch contact categories (the main type needed for contact list)
      const contactResponse = await residentApi.getCategoriesByType('contact');
      
      // Validate response structure
      if (!contactResponse || !contactResponse.data) {
        throw new Error('Invalid response from server: missing data');
      }
      
      // Ensure data is an array
      if (!Array.isArray(contactResponse.data)) {
        console.error('Expected array but got:', typeof contactResponse.data, contactResponse.data);
        throw new Error('Invalid response format: data is not an array');
      }
      
      const contactCategories = contactResponse.data;

      // Log categories for debugging
      console.log('Resident categories fetched:', contactCategories.map((c: Category) => ({ id: c.id, name: c.name })));

      // Convert flat categories to CategoryWithChildren format
      // Since resident categories are flat, we wrap them in the expected structure
      const categoriesWithChildren: CategoryWithChildren[] = contactCategories.map((cat: Category) => ({
        ...cat,
        children: [],
        isDeletable: cat.isDeletable ?? false
      }));

      setCategories(categoriesWithChildren);
    } catch (err) {
      console.error('Error fetching resident categories:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(errorMessage);
      setCategories([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    refreshCategories: fetchCategories
  };
}

